import React from "react";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import CtaBlock from "@/components/CtaBlock";
import LeadForm from "@/components/LeadForm";
import { CheckCircle2, Star, Zap, Percent, Clock, ShieldCheck } from "lucide-react";

const INCLUDED = [
  { i: Clock, t: "Annual Precision Tune-Ups", d: "Keep your system efficient and catch problems before they become breakdowns." },
  { i: Zap, t: "Priority Scheduling", d: "Members go to the front of the line, even during peak season." },
  { i: Percent, t: "Exclusive Repair Discounts", d: "Save on any repairs your system needs throughout the year." },
  { i: ShieldCheck, t: "No After-Hours Fees", d: "Emergency service without the overtime surcharge." },
];

export default function MaintenancePlan() {
  const { company } = useCompany();
  const faqs = [
    { q: "What's included in the membership?", a: "Scheduled tune-ups, priority dispatch, repair discounts, and waived after-hours fees. It's designed to prevent breakdowns and save you money." },
    { q: "How often are tune-ups performed?", a: "At least annually — ideally in spring before peak cooling. Homes that also use heating can schedule a fall check too." },
    { q: "Can I cancel anytime?", a: "Yes. Our membership is built around value, not lock-in. Contact us with any questions about your plan." },
  ];
  return (
    <>
      <Seo title="HVAC Maintenance Plan & Membership | Aquila Air & Heating" description="Join the Aquila Air & Heating maintenance membership for annual tune-ups, priority service, repair discounts, and no after-hours fees across the Space Coast." path="/maintenance-plan/" />
      <section className="bg-navy text-white py-12 md:py-16 dot-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Maintenance Plan" }]} /></div>
          <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><Star className="h-4 w-4" /> The Aquila Comfort Club</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">HVAC Maintenance Membership</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200">Protect your investment and never sweat a Florida summer breakdown. Our membership keeps your system running at peak performance all year.</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy">Membership Benefits</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {INCLUDED.map((b, i) => {
                const Icon = b.i;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`plan-benefit-${i}`}>
                    <Icon className="h-8 w-8 text-cool" />
                    <h3 className="mt-3 font-semibold text-navy">{b.t}</h3>
                    <p className="mt-1 text-sm text-slate-600">{b.d}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 bg-navy text-white rounded-xl p-6 sm:p-8 dot-grid">
              <h3 className="text-2xl font-bold">Peace of Mind, All Year</h3>
              <p className="mt-2 text-slate-200">A maintained system uses less energy, lasts longer, and breaks down far less often. For most homeowners, the membership pays for itself.</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {["Lower energy bills", "Extended equipment life", "Fewer emergency repairs", "Protected manufacturer warranty"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 className="h-4 w-4 text-cool" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
          <aside>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
              <h3 className="text-xl font-bold text-navy">Join the Club</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">Sign up and we'll schedule your first tune-up.</p>
              <LeadForm defaultService="Maintenance Plans" compact />
            </div>
          </aside>
        </div>
      </section>
      <FaqAccordion faqs={faqs} />
      <CtaBlock heading="Ready to Join the Aquila Comfort Club?" />
    </>
  );
}
